import type { Candle, CandleInterval } from '../types';

export interface CmVixFixUcsSqueezeInputs {
  useAlternativeTimeframe: boolean;
  pd: number;
  bbl: number;
  mult: number;
  lb: number;
  ph: number;
  pl: number;
  hp: boolean;
  sd: boolean;
  scale: number;
  length: number;
  multBB: number;
  multKC: number;
  smooth: number;
  usebbr: boolean;
  useHAC: boolean;
  useTrueRange: boolean;
  alternativeTimeframe: CandleInterval;
}

const DEFAULT_INPUTS: CmVixFixUcsSqueezeInputs = {
  useAlternativeTimeframe: false,
  pd: 22,
  bbl: 20,
  mult: 2,
  lb: 50,
  ph: 0.85,
  pl: 1.01,
  hp: false,
  sd: false,
  scale: 1,
  length: 20,
  multBB: 2,
  multKC: 1.5,
  smooth: 20,
  usebbr: true,
  useHAC: true,
  useTrueRange: true,
  alternativeTimeframe: '1h',
};

export type CmVixFixMomentumState =
  | 'positiveRising'
  | 'positiveFalling'
  | 'negativeFalling'
  | 'negativeRising';

export type CmVixFixSqueezeState = 'on' | 'off' | 'neutral';

export interface CmVixFixUcsSqueezeOverlayResult {
  wvf: number[];
  wvfSpike: boolean[];
  upperBand: (number | undefined)[];
  rangeHigh: number[];
  rangeLow: number[];
  momentum: (number | undefined)[];
  momentumState: (CmVixFixMomentumState | undefined)[];
  squeezeState: CmVixFixSqueezeState[];
  info: string;
}

function toPositiveInteger(value: number, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(1, Math.round(value));
}

function toNumber(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function normalizeInputs(inputs: Partial<CmVixFixUcsSqueezeInputs> | undefined): CmVixFixUcsSqueezeInputs {
  const candidate = inputs ?? {};
  return {
    useAlternativeTimeframe: candidate.useAlternativeTimeframe ?? DEFAULT_INPUTS.useAlternativeTimeframe,
    pd: toPositiveInteger(candidate.pd ?? DEFAULT_INPUTS.pd, DEFAULT_INPUTS.pd),
    bbl: toPositiveInteger(candidate.bbl ?? DEFAULT_INPUTS.bbl, DEFAULT_INPUTS.bbl),
    mult: Math.max(0.01, toNumber(candidate.mult ?? DEFAULT_INPUTS.mult, DEFAULT_INPUTS.mult)),
    lb: toPositiveInteger(candidate.lb ?? DEFAULT_INPUTS.lb, DEFAULT_INPUTS.lb),
    ph: Math.max(0, toNumber(candidate.ph ?? DEFAULT_INPUTS.ph, DEFAULT_INPUTS.ph)),
    pl: Math.max(0, toNumber(candidate.pl ?? DEFAULT_INPUTS.pl, DEFAULT_INPUTS.pl)),
    hp: candidate.hp ?? DEFAULT_INPUTS.hp,
    sd: candidate.sd ?? DEFAULT_INPUTS.sd,
    scale: Math.max(0, toNumber(candidate.scale ?? DEFAULT_INPUTS.scale, DEFAULT_INPUTS.scale)),
    length: toPositiveInteger(candidate.length ?? DEFAULT_INPUTS.length, DEFAULT_INPUTS.length),
    multBB: Math.max(0.01, toNumber(candidate.multBB ?? DEFAULT_INPUTS.multBB, DEFAULT_INPUTS.multBB)),
    multKC: Math.max(0.01, toNumber(candidate.multKC ?? DEFAULT_INPUTS.multKC, DEFAULT_INPUTS.multKC)),
    smooth: toPositiveInteger(candidate.smooth ?? DEFAULT_INPUTS.smooth, DEFAULT_INPUTS.smooth),
    usebbr: candidate.usebbr ?? DEFAULT_INPUTS.usebbr,
    useHAC: candidate.useHAC ?? DEFAULT_INPUTS.useHAC,
    useTrueRange: candidate.useTrueRange ?? DEFAULT_INPUTS.useTrueRange,
    alternativeTimeframe: candidate.alternativeTimeframe ?? DEFAULT_INPUTS.alternativeTimeframe,
  };
}

function rollingHighest(values: number[], length: number, endIndex: number): number {
  const start = Math.max(0, endIndex - length + 1);
  let highest = Number.NEGATIVE_INFINITY;
  for (let index = start; index <= endIndex; index += 1) {
    if (values[index] > highest) {
      highest = values[index];
    }
  }
  return highest;
}

function rollingLowest(values: number[], length: number, endIndex: number): number {
  const start = Math.max(0, endIndex - length + 1);
  let lowest = Number.POSITIVE_INFINITY;
  for (let index = start; index <= endIndex; index += 1) {
    if (values[index] < lowest) {
      lowest = values[index];
    }
  }
  return lowest;
}

function smaSeries(values: (number | undefined)[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (length <= 0) {
    return output;
  }

  let sum = 0;
  let valid = 0;
  for (let index = 0; index < values.length; index += 1) {
    const current = values[index];
    if (current !== undefined) {
      sum += current;
      valid += 1;
    }

    if (index >= length) {
      const dropped = values[index - length];
      if (dropped !== undefined) {
        sum -= dropped;
        valid -= 1;
      }
    }

    if (index >= length - 1 && valid === length) {
      output[index] = sum / length;
    }
  }

  return output;
}

function stdevSeries(values: (number | undefined)[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (length <= 0) {
    return output;
  }

  for (let index = length - 1; index < values.length; index += 1) {
    let sum = 0;
    let valid = 0;
    for (let bar = index - length + 1; bar <= index; bar += 1) {
      const value = values[bar];
      if (value === undefined) {
        valid = -1;
        break;
      }
      sum += value;
      valid += 1;
    }

    if (valid !== length) {
      continue;
    }

    const mean = sum / length;
    let variance = 0;
    for (let bar = index - length + 1; bar <= index; bar += 1) {
      const value = values[bar] as number;
      const delta = value - mean;
      variance += delta * delta;
    }

    output[index] = Math.sqrt(variance / length);
  }

  return output;
}

function trueRangeSeries(candles: Candle[]): number[] {
  const ranges: number[] = new Array(candles.length).fill(0);

  for (let index = 0; index < candles.length; index += 1) {
    const current = candles[index];
    if (index === 0) {
      ranges[index] = current.high - current.low;
      continue;
    }

    const previousClose = candles[index - 1].close;
    const highLow = current.high - current.low;
    const highPrevClose = Math.abs(current.high - previousClose);
    const lowPrevClose = Math.abs(current.low - previousClose);
    ranges[index] = Math.max(highLow, highPrevClose, lowPrevClose);
  }

  return ranges;
}

function lastFinite(values: (number | undefined)[]): number | undefined {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (value !== undefined && Number.isFinite(value)) {
      return value;
    }
  }
  return undefined;
}

function formatValue(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) {
    return 'n/a';
  }
  return value.toFixed(4);
}

function squeezeStateLabel(state: CmVixFixSqueezeState): string {
  if (state === 'on') {
    return 'Squeeze ON';
  }
  if (state === 'off') {
    return 'Squeeze OFF';
  }
  return 'No Squeeze';
}

export function computeCmVixFixUcsSqueezeOverlay(
  candles: Candle[],
  inputOverrides?: Partial<CmVixFixUcsSqueezeInputs>,
): CmVixFixUcsSqueezeOverlayResult | null {
  if (candles.length < 5) {
    return null;
  }

  const inputs = normalizeInputs(inputOverrides);
  const closes = candles.map((candle) => candle.close);
  const lows = candles.map((candle) => candle.low);
  const source = candles.map((candle) =>
    inputs.useHAC ? (candle.open + candle.high + candle.low + candle.close) / 4 : candle.close,
  );

  const wvf = closes.map((_, index) => {
    const highestClose = rollingHighest(closes, inputs.pd, index);
    if (highestClose === 0) {
      return 0;
    }
    return ((highestClose - lows[index]) / highestClose) * inputs.scale;
  });

  const wvfSeries: (number | undefined)[] = [...wvf];
  const wvfMidLine = smaSeries(wvfSeries, inputs.bbl);
  const wvfStdev = stdevSeries(wvfSeries, inputs.bbl).map((value) =>
    value === undefined ? undefined : inputs.mult * value,
  );
  const upperBand = wvf.map((_, index) => {
    const mid = wvfMidLine[index];
    const stdev = wvfStdev[index];
    if (mid === undefined || stdev === undefined) {
      return undefined;
    }
    return mid + stdev;
  });

  const rangeHigh = wvf.map((_, index) => rollingHighest(wvf, inputs.lb, index) * inputs.ph);
  const rangeLow = wvf.map((_, index) => rollingLowest(wvf, inputs.lb, index) * inputs.pl);
  const wvfSpike = wvf.map((value, index) => {
    const band = upperBand[index];
    return (band !== undefined && value >= band) || value >= rangeHigh[index];
  });

  const basis = smaSeries(source, inputs.length);
  const deviation = stdevSeries(source, inputs.length).map((value) =>
    value === undefined ? undefined : value * inputs.multBB,
  );
  const upperBb = source.map((_, index) => {
    const mean = basis[index];
    const dev = deviation[index];
    if (mean === undefined || dev === undefined) {
      return undefined;
    }
    return mean + dev;
  });
  const lowerBb = source.map((_, index) => {
    const mean = basis[index];
    const dev = deviation[index];
    if (mean === undefined || dev === undefined) {
      return undefined;
    }
    return mean - dev;
  });

  const kcRange = inputs.useTrueRange
    ? trueRangeSeries(candles)
    : candles.map((candle) => candle.high - candle.low);
  const kcMa = smaSeries(source, inputs.length);
  const kcRangeMa = smaSeries(kcRange, inputs.length);
  const upperKc = source.map((_, index) => {
    const ma = kcMa[index];
    const rangeMa = kcRangeMa[index];
    if (ma === undefined || rangeMa === undefined) {
      return undefined;
    }
    return ma + rangeMa * inputs.multKC;
  });
  const lowerKc = source.map((_, index) => {
    const ma = kcMa[index];
    const rangeMa = kcRangeMa[index];
    if (ma === undefined || rangeMa === undefined) {
      return undefined;
    }
    return ma - rangeMa * inputs.multKC;
  });

  const squeezeState: CmVixFixSqueezeState[] = source.map((_, index) => {
    const lowerBand = lowerBb[index];
    const upperBandAtIndex = upperBb[index];
    const lowerChannel = lowerKc[index];
    const upperChannel = upperKc[index];
    if (
      lowerBand === undefined ||
      upperBandAtIndex === undefined ||
      lowerChannel === undefined ||
      upperChannel === undefined
    ) {
      return 'neutral';
    }

    const sqzOn = lowerBand > lowerChannel && upperBandAtIndex < upperChannel;
    const sqzOff = lowerBand < lowerChannel && upperBandAtIndex > upperChannel;
    if (sqzOn) {
      return 'on';
    }
    if (sqzOff) {
      return 'off';
    }
    return 'neutral';
  });

  const momentumRaw: (number | undefined)[] = source.map((value, index) => {
    if (inputs.usebbr) {
      const upper = upperBb[index];
      const lower = lowerBb[index];
      if (upper === undefined || lower === undefined) {
        return undefined;
      }
      const span = upper - lower;
      if (span === 0) {
        return 0;
      }
      return (value - lower) / span - 0.5;
    }

    if (index < 12 || closes[index - 12] === 0) {
      return undefined;
    }
    return ((closes[index] - closes[index - 12]) / closes[index - 12]) * 100;
  });

  const momentum = smaSeries(momentumRaw, inputs.smooth);
  const momentumState = momentum.map((value, index) => {
    if (value === undefined) {
      return undefined;
    }

    const previous = momentum[index - 1] ?? 0;
    if (value > 0) {
      return value > previous ? 'positiveRising' : 'positiveFalling';
    }
    return value < previous ? 'negativeFalling' : 'negativeRising';
  });

  const latestSqueezeState = squeezeState[squeezeState.length - 1] ?? 'neutral';
  const latestWvf = lastFinite(wvf);
  const latestMomentum = lastFinite(momentum);

  return {
    wvf,
    wvfSpike,
    upperBand,
    rangeHigh,
    rangeLow,
    momentum,
    momentumState,
    squeezeState,
    info: `CM VixFix + Squeeze: WVF ${formatValue(latestWvf)} | Momentum ${formatValue(latestMomentum)} | ${squeezeStateLabel(latestSqueezeState)}`,
  };
}
